import { ItemConfig, ILineConfig, LineShapeSide, IDiagramConfig } from "../types";

interface IPoint {
	x: number;
	y: number;
}

function getConnectionPoint(shape: ItemConfig, gap, side: string): IPoint {
	const x = shape.x;
	const y = shape.y;

	switch (side) {
		case "top":
			return shape.$item.getPoint(x + shape.width / 2, y - gap);
		case "bottom":
			return shape.$item.getPoint(x + shape.width / 2, y + shape.height + gap);
		case "left":
			return shape.$item.getPoint(x - gap, y + shape.height / 2);
		case "right":
			return shape.$item.getPoint(x + shape.width + gap, y + shape.height / 2);
		case "center":
			return shape.$item.getPoint(x + shape.width / 2, y + shape.height / 2);
	}
}

function getLength(from: ItemConfig, to: ItemConfig): number {
	const x = to.x - from.x;
	const y = to.y - from.y;

	return Math.sqrt(x * x + y * y);
}

function getRoundedCorners(na: IPoint, nb: IPoint, aa: IPoint, bb: IPoint, turn: IPoint, radius = 10) {
	if (!turn) {
		const byY = aa.y === bb.y ? +radius : 0;
		const byX = aa.x === bb.x ? +radius : 0;
		return [
			na,
			{ x1: aa.x, y1: aa.y, x: aa.x + byY, y: aa.y + byX },
			{ x: bb.x - byY, y: bb.y - byX },
			{ x1: bb.x, y1: bb.y, x: nb.x, y: nb.y },
		];
	}
	const bdX = bb.x < turn.x ? -1 : 1;
	const bdY = bb.y < turn.y ? -1 : 1;

	const adX = aa.x > turn.x ? 1 : -1;
	const adY = aa.y > turn.y ? 1 : -1;

	const before = { ...turn };
	const after = { ...bb };
	let curvedTurn = {
		x1: turn.x,
		y1: turn.y,
		x: turn.x,
		y: turn.y + radius * bdY,
	} as any;

	if (aa.x === turn.x) {
		before.y += radius * adY;
		after.x -= radius * bdX;
		curvedTurn = {
			x1: turn.x,
			y1: turn.y,
			x: turn.x + radius * bdX,
			y: turn.y,
		};
	}
	if (aa.y === turn.y) {
		before.x += radius * adX;
		after.y -= radius * bdY;
		curvedTurn = {
			x1: turn.x,
			y1: turn.y,
			x: turn.x,
			y: turn.y + radius * bdY,
		};
	}

	return [na, aa, before, curvedTurn, after, { x1: bb.x, y1: bb.y, x: nb.x, y: nb.y }];
}

function getCurvedLine(na, nb, turn) {
	return [na, { x1: turn.x, y1: turn.y, x: nb.x, y: nb.y }];
}

const SIDES = ["top", "bottom", "right", "left", "center"];

function getRoutePoints(
	link: ILineConfig,
	from: ItemConfig,
	to: ItemConfig,
	gap: number,
	customGap?: number,
	fromSide: LineShapeSide | "" = "",
	toSide: LineShapeSide | "" = ""
): IPoint[] {
	// start points from shape border
	let borderFrom;
	let borderTo;
	// start points with gaps
	let gapFrom;
	let gapTo;

	let toGapValue = customGap && customGap > gap ? customGap : gap || 0;

	if (fromSide === "center" && toSide === "center") {
		return [
			{ x: from.x + from.width / 2, y: from.y + from.height / 2 },
			{ x: to.x + to.width / 2, y: to.y + to.height / 2 },
		];
	}

	if (fromSide) {
		borderFrom = getConnectionPoint(from, 0, fromSide);
		gapFrom = getConnectionPoint(from, gap, fromSide);
	}
	if (toSide) {
		borderTo = getConnectionPoint(to, 0, toSide);
		gapTo = getConnectionPoint(to, toGapValue, toSide);
	}

	if (!fromSide || !toSide) {
		const closestPoints = SIDES.map((sideFrom: LineShapeSide): [
			IPoint,
			IPoint,
			number,
			LineShapeSide,
			LineShapeSide
		] => {
			const fromPoint = getConnectionPoint(from, gap, sideFrom);
			return SIDES.map((sideTo: LineShapeSide): [
				IPoint,
				IPoint,
				number,
				LineShapeSide,
				LineShapeSide
			] => {
				const toPoint = getConnectionPoint(to, toGapValue, sideTo);
				const distance = getLength(fromPoint, toPoint);
				return [fromPoint, toPoint, distance, sideFrom, sideTo];
			}).sort((l, r) => l[2] - r[2])[0];
		}).sort((l, r) => l[2] - r[2])[0];

		gapFrom = closestPoints[0];
		gapTo = closestPoints[1];
		fromSide = closestPoints[3];
		toSide = closestPoints[4];

		link.fromSide = fromSide;
		link.toSide = toSide;
		borderFrom = getConnectionPoint(from, 0, fromSide);
		borderTo = getConnectionPoint(to, 0, toSide);
	}

	let sidesDistance =
		(fromSide === "bottom" && toSide === "top") || (fromSide === "top" && toSide === "bottom")
			? Math.abs(borderTo.y - borderFrom.y)
			: 0;

	sidesDistance =
		sidesDistance ||
		((fromSide === "left" && toSide === "right") || (fromSide === "right" && toSide === "left")
			? Math.abs(borderTo.x - borderFrom.x)
			: 0);

	// if we reached the gap limit
	if (sidesDistance > 0 && toGapValue + gap >= sidesDistance) {
		toGapValue = sidesDistance - gap;
		gapTo = getConnectionPoint(to, toGapValue, toSide);
	}

	let points = [];

	// preffer simple line form
	if (gapFrom.x === gapTo.x || gapFrom.y === gapTo.y) {
		if (
			(borderFrom.x === gapFrom.x && gapFrom.x === borderTo.x) ||
			(borderFrom.y === gapFrom.y && gapFrom.y === borderTo.y)
		) {
			// straight line
			points = [borderFrom, borderTo];
		} else {
			// line without central turn point
			points = [borderFrom, gapFrom, gapTo, borderTo];
			if (link.cornersRadius && link.connectType !== "straight") {
				points = getRoundedCorners(borderFrom, borderTo, gapFrom, gapTo, null, link.cornersRadius);
			}
		}
	} else {
		// most complex line form, with central turn
		const isLeftCollision = gapFrom.x < borderFrom.x && gapFrom.x < gapTo.x;
		const isBottomCollision = gapFrom.y > borderFrom.y && gapFrom.y > gapTo.y;

		let turn =
			borderFrom.x !== gapFrom.x && !isLeftCollision
				? { x: gapTo.x, y: gapFrom.y }
				: { x: gapFrom.x, y: gapTo.y };

		turn = isBottomCollision ? { x: gapTo.x, y: gapFrom.y } : turn;
		if (link.connectType === "flex") {
			points = getCurvedLine(borderFrom, borderTo, turn);
		} else if (link.cornersRadius && link.connectType !== "straight") {
			points = getRoundedCorners(borderFrom, borderTo, gapFrom, gapTo, turn, link.cornersRadius);
		} else {
			points = [borderFrom, gapFrom, turn, gapTo, borderTo];
		}
	}
	return points;
}

// For Diagram.ts!!!
export function nearestLinkPath(link: ILineConfig, from: ItemConfig, to: ItemConfig, config: any) {
	if (!from || !to) {
		return;
	}

	const points = getRoutePoints(link, from, to, config.lineGap, link.customGap, link.fromSide, link.toSide);

	if (link.connectType === "straight") {
		return (link.points = [points[0], points[points.length - 1]]);
	}

	if (link.points) {
		// without additional points
		if (link.points.length === points.length) {
			link.points = link.points.map((p, i) => {
				if (p && points[i] && !p.$custom) {
					return points[i];
				}
				return p;
			});
		} else {
			const custom = link.points.filter(p => p.$custom);
			link.points = custom.length ? link.points : points;
		}

		if (!link.$move) {
			link.points[0] = points[0];
			link.points[link.points.length - 1] = points[points.length - 1];
		}
	} else {
		link.points = points;
	}
}

// For placement helper
export function directLinkPath(link: ILineConfig, from: ItemConfig, to: ItemConfig, config: IDiagramConfig) {
	if (!link && from.assistant && from.partner) {
		return;
	}

	const x1 = from.x + (from.dx || 0);
	const y1 = from.y + (from.dy || 0);
	const x2 = to.x + (to.dx || 0);
	const y2 = to.y + (to.dy || 0);

	if ((from.dir && from.dir.includes("vertical")) || Array.isArray(from.dir)) {
		const sx = x1;
		const sy = Math.round(y1 + from.height / 2);
		const ex = x2;
		const ey = Math.round(y2 + to.height / 2);
		const gap = -Math.round(config.margin.itemX / 2) + 0.5;

		if (from.dir === "vertical") {
			// from right-middle to right middle
			link.points = [
				{ x: sx, y: sy },
				{ x: sx + gap, y: sy },
				{ x: sx + gap, y: ey },
				{ x: ex, y: ey },
			];
		}
		if (from.dir === "verticalLeft" || (Array.isArray(from.dir) && from.dir.includes("verticalLeft"))) {
			// from left-middle to right-middle
			link.points = [
				{ x: sx, y: sy },
				{ x: sx + gap, y: sy },
				{ x: sx + gap, y: ey },
				{ x: ex + to.width, y: ey },
			];
		}
		if (from.dir === "verticalRight" || (Array.isArray(from.dir) && from.dir.includes("verticalRight"))) {
			// from right-middle to left-middle
			link.points = [
				{ x: sx + from.width, y: sy },
				{ x: sx + from.width - gap, y: sy },
				{ x: sx + from.width - gap, y: ey },
				{ x: ex, y: ey },
			];
		}
	} else {
		const sx = Math.round(x1 + from.width / 2);
		const sy = y1 + from.height;

		let ex = x2 + (to.$even ? 0 : to.width);
		let ey = Math.round(y2 + to.height / 2);

		if (to.partner) {
			const sx = to.$even ? x1 + from.width : x1;
			const sy = Math.round(y1 + from.height / 2);

			let gap = Math.round(config.margin.itemX / 2);
			gap = to.$even ? gap : -gap;
			// from midle-right/left to midle-right/left
			link.points = [
				{ x: sx, y: sy },
				{ x: sx + gap, y: sy },
				{ x: sx + gap, y: ey },
				{ x: ex, y: ey },
			];
			return;
		}
		if (to.assistant) {
			if (from?.$partners?.common.length === 1) {
				const sx = x1;
				const sy = Math.round(y1 + from.height / 2);
				const gap = Math.round(config.margin.itemX / 2);

				link.points = [
					{ x: sx, y: sy },
					{ x: sx - gap, y: sy },
					{ x: sx - gap, y: ey },
					{ x: ex, y: ey },
				];
				return;
			}
			// from bottom-center to right-center or left-center
			link.points = [
				{ x: sx, y: sy },
				{ x: sx, y: sy },
				{ x: sx, y: ey },
				{ x: ex, y: ey },
			];
			return;
		}
		ex = Math.round(x2 + to.width / 2);
		ey = y2;
		const gap = from.$height - (from.$baseHeight + from.height) / 2;

		if (from?.$partners?.common.length === 1) {
			const sx = Math.round(x1 - config.margin.itemX / 2);

			link.points = [
				{ x: sx, y: sy - from.height / 2 },
				{ x: sx, y: sy + gap },
				{ x: ex, y: sy + gap },
				{ x: ex, y: ey },
			];
			return;
		}
		// from bottom-center to top-center
		link.points = [
			{ x: sx, y: sy },
			{ x: sx, y: sy + gap },
			{ x: ex, y: sy + gap },
			{ x: ex, y: ey },
		];
	}
}
